"use client";

import { PropsWithClassName } from "@/types";
import { Area, AreaChart, ResponsiveContainer, Tooltip, YAxis } from "recharts"; // Import Tooltip

function coerceTimestampMs(timestamp: number): number {
  if (timestamp > 1_000_000_000_000) {
    return timestamp; // Already in milliseconds
  }
  return timestamp * 1000; // Convert seconds to milliseconds
}

interface Props {
  readonly data: { value: number; timestamp: number }[];
  readonly positive?: boolean;
  readonly tooltip?: boolean;
}

export function SparklineChart({
  data,
  positive,
  className,
  tooltip = true, // Default to true if not provided
}: PropsWithClassName<Props>) {
  const color = positive ? "var(--pw-green)" : "var(--pw-red)";

  // Calculate a dynamic domain with padding
  const yAxisDomainWithPadding = [
    (dataMin: number) => (dataMin > 0 ? dataMin * 0.95 : dataMin * 1.05), // 5% padding
    (dataMax: number) => (dataMax > 0 ? dataMax * 1.05 : dataMax * 0.95), // 5% padding
  ];

  // Handle case with flat data (all values are the same) to prevent NaN or too tight domain
  const allValuesSame = data.every((d) => d.value === data[0]?.value);
  let finalYAxisDomain: any = yAxisDomainWithPadding;

  if (allValuesSame && data.length > 0) {
    const singleValue = data[0].value;
    // If all values are 0, default to a small range around 0
    if (singleValue === 0) {
      finalYAxisDomain = [-1, 1];
    } else {
      // Create a small range around the single value
      finalYAxisDomain = [singleValue * 0.9, singleValue * 1.1];
    }
  }

  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient
              id={`gradient-${positive ? "up" : "down"}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>

          {/* Add YAxis with dynamic domain and hide it */}
          <YAxis hide domain={finalYAxisDomain} />

          {tooltip ? (
            <Tooltip
              cursor={false} // Hides the vertical line cursor
              wrapperStyle={{ outline: "none" }} // Removes default tooltip border if needed
              contentStyle={{
                margin: 0,
                padding: 0,
                border: "none",
                background: "none",
              }} // Minimal styling
              formatter={(value: number, name: string, props: any) => {
                // props.payload contains the full data object for the hovered point
                if (
                  props.payload &&
                  typeof props.payload.timestamp === "number"
                ) {
                  const date = new Date(
                    coerceTimestampMs(props.payload.timestamp),
                  );
                  // Format the timestamp (e.g., HH:MM or short date and time)
                  const formattedTimestamp = date.toLocaleString();
                  return [
                    <div
                      key={name}
                      className="flex flex-col gap-2 rounded-lg border bg-card p-1 text-xs text-foreground"
                    >
                      <span>{formattedTimestamp}</span>
                      <span>
                        {Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(value)}
                      </span>
                    </div>,
                    undefined,
                  ];
                }
                return [value, name];
              }}
              labelFormatter={() => ""} // Hide the default label (e.g., index) as we format it above
            />
          ) : null}

          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={1.5}
            fillOpacity={1}
            fill={`url(#gradient-${positive ? "up" : "down"})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
